import {
  IQuotePrice,
  OnQuoteOfferingChange,
  QuoteOfferingGroup,
  QuoteOfferingWithChildren,
} from '@monetize/types/app';
import { ReactNode } from 'react';
import { ensureArray } from '../../../../../api/utils';
import { MButton, MFlex, MText } from '../../../../../components/Monetize';
import { useQuoteOfferingGroupActions } from '../../../../../hooks/useQuoteOfferingGroupActions';
import { useConfirmModal } from '../../../../../services/confirmModal';
import {
  formatQuoteOfferingsSubtotal,
  isQuoteOfferingGroupAddedOnQuote,
  QuoteOfferingGroupPermissions,
} from '../../../../../utils/quotes';
import { useQuoteContext } from '../../quoteContext';
import QuoteOffering from '../quoteOffering/QuoteOffering';
import { QuoteOfferingGroupBox } from './QuoteOfferingGroupBox';
import { QuoteOfferingGroupHeader } from './QuoteOfferingGroupHeader';

interface QuoteOfferingGroupContainerProps {
  group: QuoteOfferingGroup;
  /**
   * Unique per rendered container: the group id, prefixed when the same group
   * appears in more than one section of the table (see the amendment layout).
   * Drives the test ids.
   */
  containerKey: string;
  /** Table section this container sits in, when the table has more than one */
  sectionLabel?: string;
  /** 1-based position in the quote's group list — shown as "Group N" */
  groupNumber: number;
  /** The group's member offerings, used for the count and the subtotal */
  groupOfferings: QuoteOfferingWithChildren[];
  quotePrices: IQuotePrice[];
  /** Every offering on the quote — context for the group's add-offering row */
  quoteOfferings: QuoteOfferingWithChildren[];
  onChange: OnQuoteOfferingChange;
  permissions: QuoteOfferingGroupPermissions;
  /** False for a section offerings can't be added to — hides the add row */
  canAddOfferings: boolean;
  isOpen: boolean;
  onIsOpenChange: (isOpen: boolean) => void;
  children?: ReactNode;
}

/**
 * Visible container for a quote offering group. Wraps the member offering
 * rows (rendered by the table and passed as children) with a bordered box
 * and a header identifying the group's shipping/billing accounts.
 *
 * No horizontal padding is added around children — offering rows are
 * independent grids sharing the same column template, so any inset would
 * break column alignment with ungrouped rows.
 */
export const QuoteOfferingGroupContainer = ({
  group,
  containerKey,
  sectionLabel,
  groupNumber,
  groupOfferings,
  quotePrices,
  quoteOfferings,
  onChange,
  permissions,
  canAddOfferings,
  isOpen,
  onIsOpenChange,
  children,
}: QuoteOfferingGroupContainerProps) => {
  const { quoteData } = useQuoteContext();
  const { quote, fetchQuote } = quoteData;
  const { addOfferingsToGroup, deleteGroup, isPending } =
    useQuoteOfferingGroupActions(quote);
  const { showConfirmModal, closeModal: closeConfirmationModal } =
    useConfirmModal();

  const offeringCount = groupOfferings.length;
  const showAddOfferingRow = canAddOfferings && !permissions.isReadOnly;
  // A group carried over from the quote being amended belongs to the contract's
  // existing offerings; only groups added on this quote may be deleted.
  const canDeleteGroup =
    !permissions.isReadOnly && isQuoteOfferingGroupAddedOnQuote(group);
  const subtotal = formatQuoteOfferingsSubtotal(
    groupOfferings,
    quote?.currency,
  );

  const deleteGroupAndRefetch = async () => {
    const isDeleted = await deleteGroup(group);
    if (isDeleted && quote?.id) {
      await fetchQuote(quote.id);
    }
  };

  const handleDeleteGroup = () => {
    showConfirmModal({
      title: 'Delete this group?',
      description:
        'The group and its shipping/billing account assignment will be removed. Offerings in the group stay on the quote.',
      onYes: async () => {
        closeConfirmationModal();
        await deleteGroupAndRefetch();
      },
      yesButton: 'Delete Group',
      yesBtnProps: {
        variant: 'delete',
      },
      noBtnProps: {
        variant: 'cancel',
      },
    });
  };

  /**
   * New offerings created from the group's add row join the group right
   * after creation (creation itself can't carry a groupId), before the
   * table's regular ADD handling refetches the quote.
   */
  const handleGroupScopedChange: OnQuoteOfferingChange = async (
    action,
    changedQuoteOfferings,
    oldQuoteOfferingId,
    skipFetch,
    runEvaluate,
  ) => {
    if (action === 'ADD' && changedQuoteOfferings) {
      await addOfferingsToGroup(
        group,
        ensureArray(changedQuoteOfferings).map(({ id }) => id),
      );
    }
    return onChange(
      action,
      changedQuoteOfferings,
      oldQuoteOfferingId,
      skipFetch,
      runEvaluate,
    );
  };

  return (
    <QuoteOfferingGroupBox data-testid={`quote-offering-group-${containerKey}`}>
      <QuoteOfferingGroupHeader
        testIdKey={containerKey}
        sectionLabel={sectionLabel}
        group={group}
        groupNumber={groupNumber}
        offeringCount={offeringCount}
        subtotal={subtotal}
        isOpen={isOpen}
        onToggle={() => onIsOpenChange(!isOpen)}
        canDelete={canDeleteGroup}
        isActionPending={isPending}
        onDelete={handleDeleteGroup}
      />
      {isOpen && (
        <>
          {offeringCount === 0 && (
            <MFlex justify="center" align="center" gap={3} py={6} px={4}>
              <MText color="fg.muted" fontSize="sm">
                {showAddOfferingRow
                  ? 'Add offerings below, or move existing ones into this group.'
                  : 'This group does not have any offerings.'}
              </MText>
              {canDeleteGroup && (
                <MButton
                  variant="deleteOutline"
                  size="sm"
                  // Empty group next to no offerings — unlikely to be an accidental
                  // click and nothing is lost, so skip the confirmation modal.
                  onClick={() => deleteGroupAndRefetch()}
                  isDisabled={isPending}
                >
                  Delete Group
                </MButton>
              )}
            </MFlex>
          )}
          {children}
          {showAddOfferingRow && (
            <QuoteOffering
              quotePrices={quotePrices}
              onChange={handleGroupScopedChange}
              index={-1}
              quoteOfferings={quoteOfferings}
            />
          )}
        </>
      )}
    </QuoteOfferingGroupBox>
  );
};
